import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from "react"
import motionTokens from "styles/motionTokens"

import type {
  DismissReason,
  ToastInternal,
  ToastOptionsResolved,
  ToastPushOptions
} from "./types"
import { enqueueMicrotask, generateToastId, resolveOptions } from "./utils"

const EXIT_DURATION = motionTokens.duration.medium

type UseToastStoreOptions = {
  maxVisible: number
  exitTimersRef: MutableRefObject<Map<string, number>>
  autoDismissTimersRef: MutableRefObject<Map<string, number>>
  toastRefs: MutableRefObject<Map<string, HTMLDivElement | null>>
  focusQueueRef: MutableRefObject<Set<string>>
}

const useVisibleToasts = (maxVisible: number, toasts: ToastInternal[]) =>
  useMemo(() => {
    if (maxVisible <= 0) {
      return []
    }
    return toasts.slice(-maxVisible)
  }, [maxVisible, toasts])

type DismissDeps = {
  setToasts: Dispatch<SetStateAction<ToastInternal[]>>
  autoDismissTimersRef: MutableRefObject<Map<string, number>>
  exitTimersRef: MutableRefObject<Map<string, number>>
  toastRefs: MutableRefObject<Map<string, HTMLDivElement | null>>
  focusQueueRef: MutableRefObject<Set<string>>
}

const useDismissToast = ({
  setToasts,
  autoDismissTimersRef,
  exitTimersRef,
  toastRefs,
  focusQueueRef
}: DismissDeps) =>
  useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (id: string, reason: DismissReason = "manual") => {
      focusQueueRef.current.delete(id)

      setToasts((prev) => {
        const target = prev.find((toast) => toast.id === id)
        if (!target || target.status === "exiting") {
          return prev
        }

        enqueueMicrotask(() => {
          target.options.onDismiss?.()
        })

        return prev.map((toast) =>
          toast.id === id ? { ...toast, status: "exiting" as const } : toast
        )
      })

      const pendingAutoTimer = autoDismissTimersRef.current.get(id)
      if (pendingAutoTimer) {
        window.clearTimeout(pendingAutoTimer)
        autoDismissTimersRef.current.delete(id)
      }

      if (!exitTimersRef.current.has(id)) {
        const timer = window.setTimeout(() => {
          exitTimersRef.current.delete(id)
          setToasts((prev) => prev.filter((toast) => toast.id !== id))
          toastRefs.current.delete(id)
        }, EXIT_DURATION)
        exitTimersRef.current.set(id, timer)
      }
    },
    [autoDismissTimersRef, exitTimersRef, focusQueueRef, setToasts, toastRefs]
  )

const useDismissAll = (
  toasts: ToastInternal[],
  dismissToast: (id: string, reason?: DismissReason) => void
) =>
  useCallback(() => {
    toasts.forEach((toast) => {
      dismissToast(toast.id, "dismiss-all")
    })
  }, [dismissToast, toasts])

type PushDeps = {
  setToasts: Dispatch<SetStateAction<ToastInternal[]>>
  focusQueueRef: MutableRefObject<Set<string>>
}

const usePushToast = ({ setToasts, focusQueueRef }: PushDeps) =>
  useCallback(
    (options: ToastPushOptions) => {
      if (!options.message) {
        throw new Error("toast requires a message")
      }

      const { id: suppliedId, focus, ...rest } = options
      const id = suppliedId ?? generateToastId()

      setToasts((prev) => {
        const existingIndex = prev.findIndex((toast) => toast.id === id)
        if (existingIndex >= 0) {
          const updated = resolveOptions(prev[existingIndex].options, rest)
          const next = [...prev]
          next[existingIndex] = {
            ...next[existingIndex],
            options: updated,
            status: "visible"
          }
          return next
        }

        const resolved: ToastOptionsResolved = resolveOptions(undefined, rest)
        const nextToast: ToastInternal = {
          id,
          options: resolved,
          status: "visible"
        }
        return [...prev, nextToast]
      })

      if (focus) {
        focusQueueRef.current.add(id)
      }

      return id
    },
    [focusQueueRef, setToasts]
  )

type UpdateDeps = {
  setToasts: Dispatch<SetStateAction<ToastInternal[]>>
  focusQueueRef: MutableRefObject<Set<string>>
}

const useUpdateToast = ({ setToasts, focusQueueRef }: UpdateDeps) =>
  useCallback(
    (id: string, updates: Partial<ToastPushOptions>) => {
      const { focus, ...rest } = updates
      setToasts((prev) =>
        prev.map((toast) => {
          if (toast.id !== id) {
            return toast
          }
          const nextOptions = resolveOptions(
            toast.options,
            rest as ToastPushOptions
          )
          return {
            ...toast,
            options: nextOptions
          }
        })
      )

      if (focus === true) {
        focusQueueRef.current.add(id)
      } else if (focus === false) {
        focusQueueRef.current.delete(id)
      }
    },
    [focusQueueRef, setToasts]
  )

const useToastStore = ({
  maxVisible,
  exitTimersRef,
  autoDismissTimersRef,
  toastRefs,
  focusQueueRef
}: UseToastStoreOptions) => {
  const [toasts, setToasts] = useState<ToastInternal[]>([])

  const visibleToasts = useVisibleToasts(maxVisible, toasts)

  const dismissToast = useDismissToast({
    setToasts,
    autoDismissTimersRef,
    exitTimersRef,
    toastRefs,
    focusQueueRef
  })

  const dismissAll = useDismissAll(toasts, dismissToast)

  const pushToast = usePushToast({
    setToasts,
    focusQueueRef
  })

  const updateToast = useUpdateToast({
    setToasts,
    focusQueueRef
  })

  return {
    toasts,
    visibleToasts,
    dismissToast,
    dismissAll,
    pushToast,
    updateToast
  }
}

export default useToastStore
